import type { MessageKey } from '@dijitalapp/i18n';
import type { CallDto, ConversationDto } from '@dijitalapp/shared';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Link } from 'react-router';
import { api } from '../lib/api';
import { useApp } from '../lib/app-context';
import { callsSupported, type CallView } from '../lib/calls';
import { useChatStore } from '../lib/chat-context';
import { Icon } from './Icon';
import { PageEmpty, PageHeader } from './SectionNav';

const EMPTY_SUBSCRIBE = () => () => undefined;

/** Current call state (null when there is no call). */
export function useCall(): CallView | null {
  const store = useChatStore();
  return useSyncExternalStore(
    store ? (fn) => store.calls.subscribe(fn) : EMPTY_SUBSCRIBE,
    () => store?.calls.view ?? null,
  );
}

function Video({ stream, muted, className }: { stream: MediaStream | null; muted?: boolean; className: string }) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    if (ref.current && ref.current.srcObject !== stream) ref.current.srcObject = stream;
  }, [stream]);
  return <video ref={ref} className={className} autoPlay playsInline muted={muted} />;
}

function useElapsed(since: number | null): string {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!since) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [since]);
  if (!since) return '';
  const s = Math.max(0, Math.floor((now - since) / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** Call buttons in a direct conversation header. */
export function CallButtons({ conversation }: { conversation: ConversationDto }) {
  const { i18n } = useApp();
  const { t } = i18n;
  const store = useChatStore();
  const call = useCall();
  const peer = conversation.peer;
  if (!store || !peer || conversation.type !== 'direct' || !conversation.canSend || !callsSupported()) return null;
  const busy = call !== null && call.phase !== 'ended';
  const start = (media: 'audio' | 'video') => void store.calls.start(conversation.id, { id: peer.id, displayName: peer.displayName }, media);
  return (
    <>
      <button className="icon-btn" type="button" disabled={busy} onClick={() => start('video')} aria-label={t('call.video')} title={t('call.video')}>
        <Icon name="video" />
      </button>
      <button className="icon-btn" type="button" disabled={busy} onClick={() => start('audio')} aria-label={t('call.voice')} title={t('call.voice')}>
        <Icon name="phone" />
      </button>
    </>
  );
}

/** Ringing, in-call and ended views; mounted once in the shell. */
export function CallOverlay() {
  const { i18n } = useApp();
  const { t } = i18n;
  const store = useChatStore();
  const call = useCall();
  const elapsed = useElapsed(call?.connectedAt ?? null);
  if (!store || !call) return null;
  const calls = store.calls;
  const name = call.peer.displayName;
  const video = call.media === 'video';

  if (call.phase === 'incoming') {
    return (
      <div className="call-overlay call-ringing" role="alertdialog" aria-label={video ? t('call.incomingVideo', { name }) : t('call.incomingVoice', { name })}>
        <div className="call-avatar" aria-hidden>
          {name.slice(0, 1).toUpperCase()}
        </div>
        <p className="call-title">{video ? t('call.incomingVideo', { name }) : t('call.incomingVoice', { name })}</p>
        <div className="call-actions">
          <button className="btn btn-danger" type="button" onClick={() => calls.reject()}>
            {t('call.decline')}
          </button>
          {video && (
            <button className="btn" type="button" onClick={() => void calls.accept(false)}>
              {t('call.acceptAudio')}
            </button>
          )}
          <button className="btn btn-primary" type="button" onClick={() => void calls.accept(true)}>
            {t('call.accept')}
          </button>
        </div>
      </div>
    );
  }

  if (call.phase === 'ended') {
    return (
      <div className="call-overlay call-ended" role="status">
        <p className="call-title">{name}</p>
        <p>{t(`call.ended.${call.endReason ?? 'hangup'}` as MessageKey)}</p>
        <button className="btn" type="button" onClick={() => calls.dismiss()}>
          {t('common.close')}
        </button>
      </div>
    );
  }

  const status = call.phase === 'outgoing' ? t('call.calling', { name }) : call.phase === 'connecting' ? t('call.connecting') : elapsed;
  return (
    <div className={`call-overlay call-active ${video ? 'call-video' : ''}`} role="dialog" aria-label={name}>
      {video && call.remoteStream ? (
        <Video stream={call.remoteStream} className="call-remote" />
      ) : (
        <>
          <div className="call-avatar" aria-hidden>
            {name.slice(0, 1).toUpperCase()}
          </div>
          {/* Audio-only calls still need an element to play the remote stream. */}
          {call.remoteStream && <Video stream={call.remoteStream} className="visually-hidden" />}
        </>
      )}
      {video && call.localStream && !call.cameraOff && <Video stream={call.localStream} muted className="call-local" />}
      <div className="call-bar">
        <p className="call-title">{name}</p>
        <p className="call-status" aria-live="polite">
          {status}
        </p>
        <div className="call-actions">
          <button className="btn" type="button" aria-pressed={call.muted} onClick={() => calls.toggleMute()}>
            {call.muted ? `🔇 ${t('call.unmute')}` : `🎙 ${t('call.mute')}`}
          </button>
          {video && (
            <button className="btn" type="button" aria-pressed={call.cameraOff} onClick={() => calls.toggleCamera()}>
              {call.cameraOff ? `📷 ${t('call.cameraOn')}` : `🚫 ${t('call.cameraOff')}`}
            </button>
          )}
          <button className="btn btn-danger" type="button" onClick={() => calls.hangup()}>
            {t('call.hangup')}
          </button>
        </div>
        <p className="hint">🔒 {t('call.encryptedNote')}</p>
      </div>
    </div>
  );
}

/** Calls page: recent calls with call-back buttons. */
export function CallsPage() {
  const { i18n } = useApp();
  const { t } = i18n;
  const store = useChatStore();
  const call = useCall();
  const [calls, setCalls] = useState<CallDto[] | null>(null);
  const ended = call?.phase === 'ended';
  useEffect(() => {
    let alive = true;
    void api<{ calls: CallDto[] }>('GET', '/calls').then((r) => alive && setCalls(r.calls), () => alive && setCalls([]));
    return () => {
      alive = false;
    };
  }, [ended]);
  return (
    <div className="content-inner">
      <PageHeader title={t('call.history')} description={t('call.historyDesc')} />
      {calls === null && <p className="muted">{t('app.loading')}</p>}
      {calls?.length === 0 && <PageEmpty icon="phone" title={t('call.noHistory')} body={t('call.howTo')} />}
      {calls && calls.length > 0 && (
      <ul className="plain-list card call-list">
        {calls?.map((c) => {
          const missed = c.endReason === 'missed' && c.direction === 'incoming';
          const duration = c.answeredAt && c.endedAt ? Math.round((Date.parse(c.endedAt) - Date.parse(c.answeredAt)) / 1000) : null;
          return (
            <li key={c.id} className="toggle-row">
              <span className={`call-kind${missed ? ' call-missed' : ''}`} aria-hidden>
                <Icon name={c.media === 'video' ? 'video' : 'phone'} size={18} />
              </span>
              <div style={{ minWidth: 0 }}>
                <Link to={`/messages/${c.conversationId}`} className={missed ? 'call-missed' : ''}>
                  <strong>{c.peer.displayName}</strong>
                </Link>
                <div className="hint">
                  {c.direction === 'incoming' ? `↙ ${t('call.incoming')}` : `↗ ${t('call.outgoing')}`}
                  {' · '}
                  {i18n.formatDate(c.createdAt)}
                  {' · '}
                  {missed
                    ? t('call.missedLabel')
                    : duration !== null
                      ? t('call.duration', { minutes: Math.floor(duration / 60), seconds: duration % 60 })
                      : t(`call.ended.${c.endReason ?? 'hangup'}` as MessageKey)}
                </div>
              </div>
              <span className="spacer" />
              {store && (
                <button
                  className="btn"
                  type="button"
                  disabled={call !== null && call.phase !== 'ended'}
                  onClick={() => void store.calls.start(c.conversationId, { id: c.peer.id, displayName: c.peer.displayName }, c.media)}
                  aria-label={c.media === 'video' ? t('call.video') : t('call.voice')}
                >
                  {c.media === 'video' ? '🎥' : '📞'}
                </button>
              )}
            </li>
          );
        })}
      </ul>
      )}
    </div>
  );
}
